<template>
  <div class="personal-info">
    <router-link
      class="personal-info__back-link"
      :to="backLinkPath"
    >
      <svg class="personal-info__back-icon">
        <use :xlink:href="`#${chevron}`" />
      </svg>
      {{ t('common.actions.back') }}
    </router-link>
    <section class="personal-info__main card fifty-fifty-grid">
      <div class="personal-info__crm-container">
        <h1>{{ t('myPage.auth.title') }}</h1>
        <LogIn
          routeToPage="payment"
          :bookingStarted="true"
          :bookingNumber="bookingNumber"
        />
        <div class="personal-info__or-container">
          <span class="personal-info__or">{{ t('common.actions.or') }}</span>
        </div>
        <div class="personal-info__main-foot">
          <router-link
            class="button button--medium button--secondary"
            to="/reiseplanlegger/opprett-profil"
            @click="handleCreateProfile"
          >
            {{ t('myPage.auth.createAccount') }}
          </router-link>
          <router-link to="/reset-password">
            {{ t('myPage.auth.forgotPassword') }}
          </router-link>
        </div>
      </div>
      <div class="personal-info__guest-container">
        <h2>{{ t('travelPlanner.guestUser.noProfile') }}</h2>
        <router-link
          class="button button--medium button--secondary personal-info__guest-button"
          :to="{
            path: '/reiseplanlegger/betaling',
            query: {
              isRebooking: route.query.isRebooking,
              rebookingType: route.query.rebookingType,
              bookingId: bookingStore.bookingNumber || route.query.bookingId,
              itemsToReplace: route.query.itemsToReplace,
              noEquipment: route.query.noEquipment
            }
          }"
        >
          {{ t('travelPlanner.guestUser.continueAsGuest') }}
        </router-link>
        <div class="personal-info__info-container">
          <span class="personal-info__circle">
            <svg class="personal-info__info-icon">
              <use :xlink:href="`#${info}`" />
            </svg>
          </span>
          <p>
            {{ t('travelPlanner.guestUser.guestWarning') }}
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
  import { defineComponent, computed } from 'vue'
  import { useRoute } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import LogIn from '@/components/MyPage/LogIn/LogIn.vue'
  import info from '@/assets/icons/info.svg'
  import chevron from '@/assets/icons/chevron.svg'
  import { useBookingStore } from '@/stores/bookingStore'
  import { getToken } from '@/services/tokenService'

  interface PersonalInformationPageData {
    email: string
    password: string
  }

  export default defineComponent({
    name: 'PersonalInformationPage',
    components: {
      LogIn,
    },
    setup() {
      const { t } = useI18n()
      const route = useRoute()
      const bookingStore = useBookingStore()

      const backLinkPath = computed(() => {
        if (route.query.isRebooking) {
          if (route.query.noEquipment === 'true') {
            return {
              name: 'RebookingSeatReservation',
              params: { bookingNumber: bookingStore.bookingNumber || route.query.bookingId },
              query: {
                isRebooking: route.query.isRebooking,
                rebookingType: route.query.rebookingType,
                itemsToReplace: route.query.itemsToReplace
              }
            }
          } else {
            return {
              name: 'RebookingEquipmentPage',
              query: {
                isRebooking: route.query.isRebooking,
                rebookingType: route.query.rebookingType,
                bookingId: bookingStore.bookingNumber || route.query.bookingId,
                itemsToReplace: route.query.itemsToReplace
              }
            }
          }
        }
        
        return route.query.noEquipment === 'true'
          ? '/reiseplanlegger/reserveresete'
          : '/reiseplanlegger/tillegg'
      })

      const bookingNumber = computed(
        () => bookingStore.bookingNumber || undefined
      )

      const handleCreateProfile = async () => {
        if (bookingStore.bookingNumber) {
          localStorage.setItem(
            'pendingBookingNumber',
            bookingStore.bookingNumber
          )
        }
        localStorage.setItem('pendingBooking', 'true')

        // Get and store the current booking token
        // Token is already stored in localStorage as 'token'
        await getToken(false, true)
      }

      return {
        t,
        info,
        chevron,
        backLinkPath,
        handleCreateProfile,
        bookingNumber,
        route,
        bookingStore
      }
    },
  })
</script>

<style lang="scss">
  .personal-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    &__main {
      background-color: $clr-white;
      padding: 1.875rem;
    }

    &__back-link {
      @include title-medium;
      text-decoration: none;
      padding: 1rem;
      display: flex;
      align-items: center;
    }

    &__back-icon {
      width: 16px;
      height: 16px;
      transform: rotate(-90deg);
      margin-right: 0.5rem;
    }

    h1 {
      @include headline-medium;
      margin-bottom: 1.875rem;
    }

    &__or-container {
      display: flex;
      align-items: center;
      text-align: center;
      margin: 1.5rem 0;
      position: relative;

      &::before,
      &::after {
        content: '';
        flex: 1;
        border-bottom: 1px solid $clr-primary-100;
        margin: 0 1rem;
      }
    }

    &__or {
      @include body-large;
      background-color: $clr-white;
      padding: 0 1rem;
      z-index: 1;
    }

    &__main-foot {
      display: flex;
      flex-direction: column;
      gap: 1rem;

      a:last-child {
        text-align: left;
      }
    }

    &__guest-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 1rem;
      border-top: 1px solid $clr-primary-100;
      border-left: none;

      @media screen and (min-width: $breakpoint-xs) {
        padding: 1.875rem;
        border-left: 1px solid $clr-primary-100;
        border-top: none;
      }

      h2 {
        @include headline-medium;
      }
    }

    &__guest-button {
      width: 100%;
      padding: 1rem 0;
    }

    &__info-container {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;

      p {
        margin: 0;
        padding: 0;
      }
    }

    &__circle {
      width: 16px;
      height: 16px;
      background-color: yellow;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: 0.25rem;
    }

    &__info-icon {
      width: 16px;
      height: 16px;
      color: $clr-primary-900;
    }
  }
</style>
